import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Row, Tooltip } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyForm } from '~/components/ui/MyForm'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import { MySelect, MySelectDarkDropdown } from '~/components/ui/MySelect'
import { getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_modelFusionOptions } from '~/graphqls/types'
import { uniqueFunc } from '~/utils'
import styles from '../AiForm.module.css'
import { OptionsTabs } from '../OptionsTabs'
import Stepper from '../Stepper'
import compStyles from './ModelFusionComp.module.css'
import { modelFusionAtom } from './atom'
import useOptions from './useOptions'

type ModelFusionOptionsProps =
  Partial<getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_modelFusionOptions>

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 7
/** 最多可选数量 */
const max = 5

const ModelFusionItem: React.FC<{
  isActive: boolean
  isDisabled: boolean
  onChange: (val: number) => void
  onChecked: () => void
  data: ModelFusionOptionsProps
}> = ({ isActive, isDisabled, onChange, onChecked, data }) => {
  return (
    <div
      className={classNames(
        'relative rounded-lg p-1 h-[72px] text-neutral-800 font-semibold text-sm border-2 text-center cursor-pointer flex justify-center items-center',
        [[isActive ? 'border-neutral-500' : 'border-transparent']],
        {
          'cursor-not-allowed': isDisabled && !isActive,
        }
      )}
      style={{
        background: isActive
          ? 'radial-gradient(74.76% 334.54% at 49.84% 50%, #374176 0%, rgba(55, 65, 118, 0) 100%)'
          : '#23262F',
      }}
      onClick={onChecked}
    >
      {data?.isNew && (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/new.png"
          alt=""
        />
      )}
      {isActive ? (
        <div className="flex flex-col items-center">
          <div className="mb-1.5">{data.name}</div>
          <Stepper
            className={compStyles.stepper}
            step={0.05}
            min={-1}
            max={2}
            digits={2}
            value={data.weight!}
            onChange={val => onChange(val)}
          />
        </div>
      ) : (
        `${data.name}`
      )}
    </div>
  )
}

export const ModelFusionComp = () => {
  const [modelFusion, setModelFusion] = useAtom(modelFusionAtom)

  const {
    modelFusionOptions,
    recommendedModelFusionOptions,
    userTrainedModelAllOptions,
  } = useOptions()

  const [visible, setVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [target, setTarget] = useState<any | null>()
  const [searchKey, setSearchKey] = useState('')

  const tabs = Array.from(
    new Set(
      modelFusionOptions
        ?.filter(item => !!item.category)
        ?.map(item => item.category!)
    )
  )

  const isDisabled = modelFusion.length >= max

  const onChange = useCallback((item: ModelFusionOptionsProps, val: number) => {
    setModelFusion(current =>
      current.map(it => {
        if (it.key === item.key) {
          return {
            ...it,
            weight: val,
          }
        } else {
          return it
        }
      })
    )
  }, [])

  const onChecked = useCallback(
    (item: ModelFusionOptionsProps, isActive: boolean) => {
      if (isActive) {
        setModelFusion(current => current.filter(it => it.key !== item.key))
      } else {
        if (modelFusion.length >= max) return

        setModelFusion(current => [
          ...current,
          {
            key: item.key!,
            weight: item.weight!,
          },
        ])
      }
    },
    [modelFusion, max]
  )

  const filterModelFusion = (it: { key: string; [key: string]: any }) => {
    if (it.key?.startsWith('user_')) {
      return userTrainedModelAllOptions?.every(
        subIt => `user_${subIt.key}` !== it.key
      )
        ? true // 同款过来的用户训练模型 展示出来即可
        : modelFusionOptions.some(subIt => subIt?.key === it.key)
    }
    return modelFusionOptions.findIndex(option => option.key === it.key) > -1
  }
  const defaultOptions = uniqueFunc(
    [
      ...modelFusion.filter(filterModelFusion).map(item => {
        if (item.key?.startsWith('user_')) {
          return {
            ...item,
            ...modelFusionOptions.find(o => o.key === item.key),
            weight: item.weight,
          }
        }
        return {
          ...modelFusionOptions.find(o => o.key === item.key),
          weight: item.weight,
        }
      }),
      ...recommendedModelFusionOptions?.slice(0, DEFAULT_SHOW_COUNT),
    ],
    'key'
  )

  useEffect(() => {
    if (!modelFusion.length) return
    setModelFusion(modelFusion.filter(filterModelFusion))
  }, [modelFusionOptions])

  useEffect(() => {
    // 滚动到 target 位置
    setSearchKey(target?.name ?? '')
    if (!target) return
    const categoryList = modelFusionOptions.filter(
      item => !item?.category || item.category === target?.category
    )
    const rowIndex = Math.floor(
      categoryList.findIndex(
        option => !!option?.key && option?.key == target?.key
      ) / 4
    )
    const scrollTop = rowIndex * 84
    const row = document.getElementById('modelFusionGrid')
    // console.log('1 %d, %d, %d', currentIndex, rowIndex, scrollTop)
    if (!!row) {
      row.scrollTop = scrollTop
    }
  }, [target])

  if (modelFusionOptions.length === 0) return <></>

  return (
    <>
      <div className="w-full flex justify-between items-center mb-1">
        <span>
          融合模型{' '}
          <Tooltip
            placement="topLeft"
            arrowPointAtCenter
            title="将基础模型和其它模型融合，分别设置权重。模型权重取值在-1-2之间"
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
        <a
          href="https://support.wujiebantu.com/qa/lora.html"
          target="_blank"
          rel="noreferrer"
          className="flex items-center text-xs pl-2 pr-0.5 rounded-3xl bg-[#281A54] text-primary"
        >
          使用说明
          <MyIcon icon="arrow-right-simple" className="text-[20px]" />
        </a>
      </div>

      <MyForm.Item className={classNames(styles.borderLessFormItem, '!mb-5')}>
        <Row gutter={12}>
          {defaultOptions.map(item => {
            const currentItem = modelFusion.find(
              it => (it.key as string) === item.key
            )
            const isActive =
              modelFusion.findIndex(it => (it.key as string) === item.key) > -1
            return (
              <Col key={item.key} span={6} className="mb-3">
                <ModelFusionItem
                  isActive={isActive}
                  isDisabled={isDisabled}
                  data={{ ...item, ...currentItem }}
                  onChecked={() => {
                    onChecked(item, isActive)
                  }}
                  onChange={val => {
                    onChange(item, val)
                  }}
                />
              </Col>
            )
          })}
          <Col span={6} className="mb-3">
            <div
              className="rounded-lg p-1 h-[72px] text-neutral-800 font-semibold text-sm border-2 border-neutral-300 text-center cursor-pointer flex items-center justify-center"
              style={{
                background: 'rgba(35, 38, 47, 0.32)',
              }}
              onClick={() => setVisible(true)}
            >
              <MyIcon icon={'more1'} className="font-normal" />
              <span className="ml-0.5">更多</span>
            </div>
          </Col>
        </Row>
      </MyForm.Item>
      <MyModal
        open={visible}
        title={
          <div className="flex items-center">
            <>
              融合模型 ({modelFusion.length}/{max})
            </>
            <div className="relative items-center ml-[16px]">
              <MySelect
                className={classNames('w-[167px]', compStyles.select)}
                $dark={true}
                $rounded={true}
                placeholder="搜索"
                dropdownStyle={{
                  borderRadius: 12,
                  background: '#23262F',
                  padding: '8px 0',
                }}
                value={target?.name}
                showSearch
                filterOption={(input, option) => {
                  return (option?.value ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }}
                dropdownRender={menu => {
                  return <MySelectDarkDropdown>{menu}</MySelectDarkDropdown>
                }}
                onSelect={(val: any) => {
                  const target = modelFusionOptions.find(
                    option => option.name === val
                  )
                  if (!!target) {
                    const nextTabIndex = tabs.findIndex(
                      tab => tab === target?.category
                    )
                    if (nextTabIndex >= 0) {
                      setCurrentIndex(nextTabIndex)
                      setTarget(target)
                      if (!modelFusion.find(it => it.key === target!!.key)) {
                        onChecked(target, false)
                      }
                    }
                  }
                }}
                onSearch={(val: any) => {
                  setSearchKey(val)
                }}
                suffixIcon={
                  searchKey.length <= 0 && (
                    <MyIcon
                      icon="search"
                      className="text-neutral-400 text-[20px]"
                    ></MyIcon>
                  )
                }
              >
                {modelFusionOptions.map(option => (
                  <MySelect.Option key={option.key} value={option.name}>
                    {option.name}
                  </MySelect.Option>
                ))}
              </MySelect>
              {searchKey.length > 0 && (
                <div
                  id="gridSelectSearchClear"
                  className="absolute right-[11px] top-[4px]"
                  onClick={() => {
                    setTarget(null)
                  }}
                >
                  <MyIcon
                    icon="close"
                    className="text-neutral-400 text-[20px]"
                  />
                </div>
              )}
            </div>
          </div>
        }
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={1440}
        footer={null}
        centered
        isFullScreen
      >
        <OptionsTabs
          options={tabs?.map(item => {
            return {
              label: item,
            }
          })}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />
        <div
          id="modelFusionGrid"
          className="flex content-start mb-3 flex-wrap mb-3 min-h-[100px] overflow-auto scrollbar gap-3"
          style={{
            height: `calc(100vh - 216px)`, // 216px为其他元素的高度+间距
          }}
        >
          {modelFusionOptions
            .filter(
              item => !item.category || item.category === tabs[currentIndex]
            )
            .map(item => {
              const currentItem = modelFusion.find(
                it => (it.key as string) === item.key
              )
              const isActive =
                modelFusion.findIndex(it => (it.key as string) === item.key) >
                -1
              return (
                <div
                  key={item.key}
                  style={{
                    width: 'calc((100% - 84px) / 8)',
                  }}
                >
                  <ModelFusionItem
                    key={item.key}
                    isActive={isActive}
                    isDisabled={isDisabled}
                    data={{ ...item, ...currentItem }}
                    onChecked={() => {
                      onChecked(item, isActive)
                    }}
                    onChange={val => {
                      onChange(item, val)
                    }}
                  />
                </div>
              )
            })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setModelFusion([])
              setVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
